/**
 * BundleDealCategories Web Component
 * Manages category filter buttons, skeleton shimmer state, and sync with Searchanise sidebar/sorting controls.
 */
class BundleDealCategories extends HTMLElement {
  constructor() {
    super();
  }

  /**
   * Lifecycle Callback: Invoked when component is attached to the DOM.
   * Initializes buttons, skeleton polling, event listeners, and initial state sync.
   */
  connectedCallback() {
    this.filterButtons = this.querySelectorAll('.bundle-filter-btn');
    if (!this.filterButtons.length) return;

    this.lastClickedButtonId = null;

    this.setupSkeletonState();
    this.bindEvents();
    this.syncState();
  }

  /**
   * Lifecycle Callback: Invoked when component is removed from the DOM.
   * Cleans up background interval timers and document event listeners.
   */
  disconnectedCallback() {
    if (this.skeletonTimeout) clearTimeout(this.skeletonTimeout);
    if (this.onDocumentChange) document.removeEventListener('change', this.onDocumentChange);
    if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    if (this.onFiltersUpdated) document.removeEventListener('searchanise:filters-updated', this.onFiltersUpdated);
  }

  /**
   * Helper Function: Find all corresponding gender checkboxes across desktop and mobile Searchanise sidebar layouts.
   * @param {string} filterType - 'men', 'women', or 'unisex'
   * @returns {HTMLInputElement[]} - Array of matching checkbox elements.
   */
  getGenderCheckboxes(filterType) {
    const genderWidgets = document.querySelectorAll('[data-attr-key*="gender" i], [data-title*="gender" i]');
    const widgets = genderWidgets.length ? genderWidgets : document.querySelectorAll('[data-sidebar-container], .sidebar-mob, .col-sidebar, .sidebar-filter');
    const matchedCheckboxes = [];
    widgets.forEach(sidebarWidget => {
      for (const genderCheckbox of sidebarWidget.querySelectorAll('input[type="checkbox"]')) {
        let labelText = genderCheckbox.value || '';
        if (genderCheckbox.id) {
          const safeId = CSS.escape(genderCheckbox.id);
          const label = sidebarWidget.querySelector(`label[for="${safeId}"]`);
          if (label) labelText += ' ' + label.textContent;
        }
        const parentLabel = genderCheckbox.closest('label');
        if (parentLabel) labelText += ' ' + parentLabel.textContent;
        const normalized = labelText.toLowerCase();

        const isUnisexText = /\b(unisex|both|his & her|him & her|his and her|him and her)\b/i.test(normalized);
        const isWomenText = !isUnisexText && /\b(women|woman|her|for her)\b/i.test(normalized);
        const isMenText = !isUnisexText && !isWomenText && /\b(men|man|him|for him)\b/i.test(normalized);

        if (filterType === 'men' && isMenText) {
          if (!matchedCheckboxes.includes(genderCheckbox)) matchedCheckboxes.push(genderCheckbox);
        } else if (filterType === 'women' && isWomenText) {
          if (!matchedCheckboxes.includes(genderCheckbox)) matchedCheckboxes.push(genderCheckbox);
        } else if (filterType === 'unisex' && isUnisexText) {
          if (!matchedCheckboxes.includes(genderCheckbox)) matchedCheckboxes.push(genderCheckbox);
        }
      }
    });
    return matchedCheckboxes;
  }

  /**
   * Helper Function: Check if any gender checkbox for the given filterType is checked.
   * @param {string} filterType - 'men', 'women', or 'unisex'
   * @returns {boolean}
   */
  isGenderChecked(filterType) {
    const cbs = this.getGenderCheckboxes(filterType);
    if (!cbs.length) return false;
    return cbs.some(cb => cb.checked);
  }

  /**
   * Helper Function: Check if Searchanise sidebar filter controls are rendered and ready.
   * @returns {boolean}
   */
  isFilterReady() {
    return !!(this.getGenderCheckboxes('men').length || this.getGenderCheckboxes('women').length || this.getGenderCheckboxes('unisex').length);
  }

  /**
   * Helper Function: Remove skeleton loading state from all category buttons.
   */
  setButtonsReady() {
    this.filterButtons.forEach(buttonElement => buttonElement.classList.remove('skeleton-loading'));
  }

  /**
   * Helper Function: Show skeleton loader and wait for Searchanise sidebar filters to finish rendering via custom event.
   */
  setupSkeletonState() {
    if (this.isFilterReady()) {
      this.setButtonsReady();
      return;
    }

    this.filterButtons.forEach(buttonElement => buttonElement.classList.add('skeleton-loading'));

    const removeSkeletonOnReady = () => {
      if (this.isFilterReady()) {
        this.setButtonsReady();
        document.removeEventListener('searchanise:filters-updated', removeSkeletonOnReady);
        if (this.skeletonTimeout) clearTimeout(this.skeletonTimeout);
      }
    };

    document.addEventListener('searchanise:filters-updated', removeSkeletonOnReady);

    // Fallback safety timeout (5 seconds) in case filters take too long or fail to render
    this.skeletonTimeout = setTimeout(() => {
      this.setButtonsReady();
      document.removeEventListener('searchanise:filters-updated', removeSkeletonOnReady);
    }, 5000);
  }

  /**
   * Helper Function: Bind click listeners on category buttons and document-level sync listeners.
   */
  bindEvents() {
    this.onButtonClick = this.handleButtonClick.bind(this);
    this.filterButtons.forEach(buttonElement => {
      buttonElement.addEventListener('click', this.onButtonClick);
    });

    this.onDocumentChange = (event) => {
      if (event.target.matches('input[type="checkbox"]') || event.target.matches('select')) {
        this.syncState();
      }
    };

    this.onDocumentClick = (event) => {
      if (event.target.closest('[data-sortby]')) {
        setTimeout(() => this.syncState(), 50);
      }
    };

    this.onFiltersUpdated = () => this.syncState();

    document.addEventListener('change', this.onDocumentChange);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('searchanise:filters-updated', this.onFiltersUpdated);
  }

  /**
   * Helper Function: Restore accessibility focus to the last clicked button.
   */
  restoreFocus() {
    if (this.lastClickedButtonId) {
      const btnToFocus = this.querySelector(`[data-btn-id="${this.lastClickedButtonId}"]`);
      if (btnToFocus && document.activeElement !== btnToFocus) {
        btnToFocus.focus({ preventScroll: true });
      }
    }
  }

  /**
   * Helper Function: Handle click events on category buttons (Men, Women, Trending).
   * @param {Event} event - The click event object.
   */
  handleButtonClick(event) {
    event.preventDefault();
    const buttonElement = event.currentTarget;
    if (buttonElement.classList.contains('skeleton-loading')) return;

    this.lastClickedButtonId = buttonElement.getAttribute('data-btn-id');

    const filterType = buttonElement.getAttribute('data-filter-type');
    const isActive = buttonElement.classList.contains('active');

    if (filterType === 'trending') {
      if (isActive) {
        this.sortBy('default');
      } else {
        this.clearFilters(true);
        this.sortBy('bestselling');
      }
      setTimeout(() => {
        this.syncState();
        this.restoreFocus();
      }, 50);
    } else {
      if (isActive) {
        const checkboxes = this.getGenderCheckboxes(filterType);
        if (checkboxes.length) {
          checkboxes.forEach(cb => { cb.checked = false; });
          checkboxes[0].dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        this.clearFilters(true);
        this.sortBy('default');
        const checkboxes = this.getGenderCheckboxes(filterType);
        if (checkboxes.length) {
          checkboxes.forEach(cb => { cb.checked = true; });
          checkboxes[0].dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      setTimeout(() => {
        this.restoreFocus();
      }, 50);
    }
  }

  /**
   * Helper Function: Reset or clear active gender filter checkboxes.
   * @param {boolean} silent - If true, prevents triggering redundant change events.
   */
  clearFilters(silent = false) {
    const checkboxes = [...this.getGenderCheckboxes('men'), ...this.getGenderCheckboxes('women'), ...this.getGenderCheckboxes('unisex')];
    let changed = false;
    checkboxes.forEach(cb => {
      if (cb.checked) {
        cb.checked = false;
        changed = true;
      }
    });
    if (changed && !silent && checkboxes.length) {
      checkboxes[0].dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Helper Function: Change collection sorting order.
   * @param {string} sortType - 'bestselling' or 'default'
   */
  sortBy(sortType) {
    const sortItems = document.querySelectorAll('[data-sortby] [data-sortby-list] li');
    for (const sortItem of sortItems) {
      const itemText = (sortItem.textContent || '').toLowerCase();
      if ((sortType === 'bestselling' && itemText.includes('bestsell')) || (sortType === 'default' && (itemText.includes('relevance') || itemText.includes('default')))) {
        sortItem.click();
        return;
      }
    }
  }

  /**
   * Helper Function: Synchronize category button active states with Searchanise sidebar/sorting state.
   */
  syncState() {
    const isMenChecked = this.isGenderChecked('men');
    const isWomenChecked = this.isGenderChecked('women');
    const isUnisexChecked = this.isGenderChecked('unisex');
    const hasGender = isMenChecked || isWomenChecked || isUnisexChecked;

    const activeSort = document.querySelector('[data-sortby] [data-sortby-list] li.active, [data-sortby] [data-sortby-list] li[aria-selected="true"]');
    const sortBtn = document.querySelector('[data-sortby] [data-sortby-btn]');
    const isBestsellSort = !!(
      (activeSort && activeSort.textContent.toLowerCase().includes('bestsell')) ||
      (sortBtn && sortBtn.textContent.toLowerCase().includes('bestsell')) ||
      (window.location.search.toLowerCase().includes('sales_amount') || window.location.search.toLowerCase().includes('bestsell'))
    );

    if (hasGender && isBestsellSort) {
      this.sortBy('default');
    }

    const isTrendingActive = isBestsellSort && !hasGender;

    let anyActive = false;

    this.filterButtons.forEach(buttonElement => {
      const filterType = buttonElement.getAttribute('data-filter-type');
      let isActive = false;
      if (filterType === 'men') isActive = isMenChecked;
      else if (filterType === 'women') isActive = isWomenChecked;
      else if (filterType === 'unisex') isActive = isUnisexChecked;
      else if (filterType === 'trending') isActive = isTrendingActive;

      buttonElement.classList.toggle('active', isActive);
      buttonElement.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (isActive) anyActive = true;
    });

    this.querySelector('.frag-cats__grid')?.classList.toggle('has-active', anyActive);
  }
}

if (!customElements.get('bundle-deal-categories')) {
  customElements.define('bundle-deal-categories', BundleDealCategories);
}

